import { useEffect, useRef, useState } from 'react';
import { 
  Sun, Moon, Camera, RotateCcw, Image as ImageIcon,
  Monitor, Upload, Smartphone, X
} from 'lucide-react';
import { CurtainSceneController, VisualizerConfig } from './CurtainSceneController';
import { WindowCalibrationOverlay } from './WindowCalibrationOverlay';
import { sampleRooms } from './sampleRooms';

export interface CurtainVisualizerCanvasProps {
  config: Omit<VisualizerConfig, 'openPercent' | 'mode'>;
}

export const CurtainVisualizerCanvas: React.FC<CurtainVisualizerCanvasProps> = ({ config }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<CurtainSceneController | null>(null);
  
  const [openPercent, setOpenPercent] = useState(50);
  const [mode, setMode] = useState<'studio' | 'ar'>('studio');
  const [isNight, setIsNight] = useState(false);
  
  const [showCalibration, setShowCalibration] = useState(false);
  const [bgImage, setBgImage] = useState<string | null>(null);

  // Initialize scene
  useEffect(() => {
    if (!containerRef.current) return;
    
    controllerRef.current = new CurtainSceneController(containerRef.current);
    
    return () => {
      controllerRef.current?.dispose();
    };
  }, []);

  // Sync config
  useEffect(() => {
    if (controllerRef.current) {
      // Small hack: if night, pass a special flag or just use the controller's existing light 
      // Right now the controller doesn't support night mode natively, but we can update it later.
      controllerRef.current.updateCurtain({
        ...config,
        openPercent,
        mode
      });
      // Optionally we could add day/night logic here by manipulating the scene lights 
      // if we had access, but for now we sync the config.
    }
  }, [config, openPercent, mode, isNight]);

  const handleSnapshot = () => {
    if (!controllerRef.current) return;
    const dataUrl = controllerRef.current.takeScreenshot();
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `curtain-visualizer-${Date.now()}.png`;
    a.click();
  };

  const handleResetOrbit = () => {
    // We would ideally call a reset method on the controller.
    // For now, this is a placeholder as the controller doesn't expose it.
    // We can add it to the controller if needed.
  };

  const startARMode = (img: string) => {
    setBgImage(img);
    setShowCalibration(true);
  };
  
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => startARMode(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="relative w-full h-full bg-gray-900 overflow-hidden flex flex-col">
      {/* 3D Canvas Container */}
      <div 
        ref={containerRef} 
        className={`w-full flex-1 ${mode === 'ar' ? 'absolute inset-0 z-10 pointer-events-none' : ''}`} 
        style={{
          // For AR, we'll let the user interact with the scene but keep the background image behind
          pointerEvents: mode === 'ar' ? 'none' : 'auto' // if AR, maybe we need to enable pointers for orbit? 
        }}
      />
      
      {/* Background Image for AR */}
      {mode === 'ar' && bgImage && !showCalibration && (
        <div className="absolute inset-0 z-0 flex items-center justify-center bg-black">
          <img src={bgImage} alt="AR Background" className="w-full h-full object-contain" />
        </div>
      )}

      {/* AR Calibration Overlay */}
      {showCalibration && bgImage && (
        <div className="absolute inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
             <div className="flex justify-between items-center bg-white p-4 rounded-t-xl">
               <h3 className="text-lg font-semibold">Calibrate Window</h3>
               <button onClick={() => setShowCalibration(false)} className="p-2 hover:bg-gray-100 rounded-full">
                 <X className="w-5 h-5" />
               </button>
             </div>
             <WindowCalibrationOverlay 
               imageUrl={bgImage}
               onConfirm={(_data) => {
                 // In a full implementation, we'd pass data.boundingBox to the controller 
                 // to align the 3D curtain with the photo.
                 setShowCalibration(false);
                 setMode('ar');
               }}
             />
          </div>
        </div>
      )}

      {/* Mode Switcher - Top Center */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-white/10 backdrop-blur-md p-1 rounded-full flex gap-1 border border-white/20">
        <button
          onClick={() => setMode('studio')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            mode === 'studio' ? 'bg-white text-gray-900 shadow-md' : 'text-white hover:bg-white/20'
          }`}
        >
          <Monitor className="w-4 h-4" /> Studio
        </button>
        <button
          onClick={() => {
            if (!bgImage) {
              startARMode(sampleRooms[0].imageUrl);
            } else {
              setMode('ar');
            }
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            mode === 'ar' ? 'bg-white text-gray-900 shadow-md' : 'text-white hover:bg-white/20'
          }`}
        >
          <ImageIcon className="w-4 h-4" /> My Window
        </button>
      </div>

      {/* AR Options Panel (when in AR mode) */}
      {mode === 'ar' && (
        <>
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 flex gap-2">
             <label className="cursor-pointer bg-white/90 backdrop-blur px-3 py-2 rounded-lg text-sm font-medium shadow flex items-center gap-2 hover:bg-white transition-colors">
               <Upload className="w-4 h-4" /> Upload
               <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
             </label>
             {/* In a real app we might use capture="environment" for live camera */}
             <label className="cursor-pointer bg-white/90 backdrop-blur px-3 py-2 rounded-lg text-sm font-medium shadow flex items-center gap-2 hover:bg-white transition-colors">
               <Smartphone className="w-4 h-4" /> Camera
               <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
             </label>
             <button 
               onClick={() => setShowCalibration(true)}
               className="bg-white/90 backdrop-blur px-3 py-2 rounded-lg text-sm font-medium shadow flex items-center gap-2 hover:bg-white transition-colors"
             >
               <RotateCcw className="w-4 h-4" /> Recalibrate
             </button>
             {bgImage && (
               <button 
                 onClick={() => {
                   setBgImage(null);
                   setMode('studio');
                 }}
                 className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm font-medium shadow flex items-center gap-2 hover:bg-red-100 transition-colors border border-red-100"
               >
                 <X className="w-4 h-4" /> Clear Photo
               </button>
             )}
          </div>
          
          {/* Sample Rooms Panel */}
          {!bgImage && (
            <div className="absolute top-36 left-1/2 -translate-x-1/2 z-40 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg border border-white/20 w-11/12 max-w-2xl">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Try a Sample Room</h4>
              <div className="grid grid-cols-3 gap-3">
                {sampleRooms.map((room, idx) => {
                  // Fallback gradient based on index if image fails or doesn't exist
                  const gradients = [
                    'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
                    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
                    'linear-gradient(135deg, #f6d365 0%, #fda085 100%)'
                  ];
                  return (
                    <button
                      key={room.id}
                      onClick={() => startARMode(room.imageUrl)}
                      className="group relative flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-white transition-colors border border-transparent hover:border-gray-200"
                    >
                      <div 
                        className="w-full aspect-video rounded-md overflow-hidden bg-gray-100 shadow-sm relative flex items-center justify-center"
                        style={{ background: gradients[idx % gradients.length] }}
                      >
                        <ImageIcon className="w-6 h-6 text-white/50 absolute z-0" />
                        <img 
                          src={room.imageUrl} 
                          alt={room.name}
                          className="w-full h-full object-cover relative z-10"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700 text-center">{room.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* HUD - Bottom Overlay */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-md bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/20">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center text-sm font-medium text-gray-700">
            <span>Fully Stacked</span>
            <span>Open/Close</span>
            <span>Fully Drawn</span>
          </div>
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={openPercent}
            onChange={(e) => setOpenPercent(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>
      </div>

      {/* Right Tools HUD */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3">
        <button 
          onClick={() => setIsNight(!isNight)}
          className="w-12 h-12 bg-white/90 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center hover:bg-white hover:scale-105 transition-all text-gray-800"
          title="Day / Night"
        >
          {isNight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        </button>
        
        <button 
          onClick={handleResetOrbit}
          className="w-12 h-12 bg-white/90 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center hover:bg-white hover:scale-105 transition-all text-gray-800"
          title="Reset Camera"
        >
          <RotateCcw className="w-5 h-5" />
        </button>

        <button 
          onClick={handleSnapshot}
          className="w-12 h-12 bg-indigo-600 rounded-full shadow-lg flex items-center justify-center hover:bg-indigo-700 hover:scale-105 transition-all text-white"
          title="Take Snapshot"
        >
          <Camera className="w-5 h-5" />
        </button>
      </div>

    </div>
  );
};
